const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/reducers/labReports/loadCumulSummaryHandlers.ts';
import Imm from 'immutable';
import moment from 'moment';
import { decCounter, incCounter } from '../../utils/reducerUtils';
import { IKisApiHandlersMap } from '../../defs/actionTypes';
import { LoadCumulSummaryAction } from '../../actions/labReportsActions';
import { Logger } from '../../services/KisLoggerService';
import { KISIM_TIME_FORMAT, KISIM_TIME_LOWER_LIMIT } from '../../defs/generalDefs';

const Log = Logger('labReports/loadCumulSummary');
const emptyList = Imm.List<any>();

export const loadCumulSummaryHandlers: IKisApiHandlersMap<LoadCumulSummaryAction> = {
    pending: (state, action) => {
        const repId = `${action.meta.patnr}_${action.meta.defsId}`;
        return incCounter(state, ['cumulReports', repId, 'loading']);
    },
    success: (state, action) => {
        const repId = `${action.meta.patnr}_${action.meta.defsId}`;
        const results = Imm.fromJS(action?.payload?.Result || []);

        let availMinDat = state.getIn(['cumulReports', repId, 'availMinDat']);
        let availMaxDat = state.getIn(['cumulReports', repId, 'availMaxDat']);

        const newMinDat = action.meta.mindat ?? '';
        const newMaxDat = action.meta.maxdat ?? '';

        if (!availMinDat || !availMaxDat) {
            // Initialize the range with the new range.
            availMinDat = newMinDat;
            availMaxDat = newMaxDat;
        } else if (
            (availMinDat < newMinDat && availMaxDat < newMinDat) ||
            (availMinDat > newMaxDat && availMaxDat > newMaxDat)
        ) {
            // The old and new range do not overlap!
            Log.warn(
                'loadCumulSummarySuccess: Dropping old availList, because the new range ' +
                    'does not overlap the old one. This is safe, but may lead to unnecessary ' +
                    'refetching of data.'
            );
            availMinDat = newMinDat;
            availMaxDat = newMaxDat;
            state = state.deleteIn(['cumulReports', repId, 'availList']);
        } else {
            // The old and new range overlap.
            availMinDat = availMinDat < newMinDat ? availMinDat : newMinDat;
            availMaxDat = availMaxDat > newMaxDat ? availMaxDat : newMaxDat;
        }

        state = state.setIn(['cumulReports', repId, 'availMinDat'], availMinDat);
        state = state.setIn(['cumulReports', repId, 'availMaxDat'], availMaxDat);

        // Add or update items in availList.

        let availList = state.getIn(['cumulReports', repId, 'availList']) || emptyList;
        const oldAvailList = availList;

        results.forEach((res: Imm.Map<string, any>) => {
            const sender = res.get('SENDER');
            const reportnr = res.get('REPORTNR');
            const foundAt = availList.reduce((result: number, res2: Imm.Map<string, any>, idx: number) => {
                if (result >= 0) {
                    return result;
                } else if (sender === res2.get('SENDER') && reportnr === res2.get('REPORTNR')) {
                    return idx;
                } else {
                    return result;
                }
            }, -1);

            if (foundAt >= 0) {
                // Merge with the existing entry.

                const oldItem = availList.get(foundAt);
                const stale = (res.get('Date') || '?') !== (oldItem.get('Date') || '?');
                res = oldItem.merge(res);

                if (stale) {
                    Log.info(`Marking merged availList item as 'stale', because the Date has changed.`);
                    res = res.set('stale', true); // indicates actual data needs to be refetched
                }

                availList = availList.set(foundAt, res);
            } else {
                // Add a new entry.
                availList = availList.push(res);
            }
        });

        // Keep availList sorted by Date (descending).

        if (oldAvailList !== availList) {
            availList = availList.sort((itemA: Imm.Map<string, any>, itemB: Imm.Map<string, any>) => {
                const datA = itemA.get('Date') || KISIM_TIME_LOWER_LIMIT;
                const datB = itemB.get('Date') || KISIM_TIME_LOWER_LIMIT;

                if (datA < datB) {
                    return 1;
                } else if (datA > datB) {
                    return -1;
                } else {
                    return 0;
                }
            });
        }

        state = state.setIn(['cumulReports', repId, 'availList'], availList);

        // Update the loading counter, and we're done!

        return decCounter(state, ['cumulReports', repId, 'loading']);
    },
    failure: (state, action) => {
        Log.warn('Failed to load the cumul summary!', action);

        const repId = `${action.meta.patnr}_${action.meta.defsId}`;
        let availMinDat = state.getIn(['cumulReports', repId, 'availMinDat']);

        if (!availMinDat) {
            // The selectors think the report is loading as long as availMinDat has not been set.
            // That's why we must set availMinDat to something here.
            availMinDat = moment().format(KISIM_TIME_FORMAT);
            state = state.setIn(['cumulReports', repId, 'availMinDat'], availMinDat);
        }

        return decCounter(state, ['cumulReports', repId, 'loading']);
    },
};
